import type { QueryClient } from "@tanstack/react-query";

/**
 * What to refetch after a mutation.
 *
 * Every mutation used to call `invalidateQueries()` with no arguments, which
 * refetches every query in the cache — including the three expensive ones
 * (`league.chassis`, `league.board`, `league.leaderboards`) whether or not they
 * could have changed. The rating-board editor did it on every rating typed.
 *
 * These take the tRPC proxy rather than hardcoded key strings, so a renamed
 * router is a type error rather than a silently dead invalidation.
 */
type Trpc = {
  league: { pathKey: () => readonly unknown[] };
  games: { pathKey: () => readonly unknown[] };
  articles: { pathKey: () => readonly unknown[] };
  auth: { pathKey: () => readonly unknown[] };
  admin: { pathKey: () => readonly unknown[] };
};

/**
 * A result changed: the game itself, and everything computed from games —
 * standings, leaderboards, the franchise views, the schedule.
 */
export function invalidateResults(qc: QueryClient, trpc: Trpc) {
  return Promise.all([
    qc.invalidateQueries({ queryKey: trpc.games.pathKey() }),
    qc.invalidateQueries({ queryKey: trpc.league.pathKey() }),
  ]);
}

/** Season settings, ratings and rosters — everything the league router serves. */
export function invalidateLeague(qc: QueryClient, trpc: Trpc) {
  return qc.invalidateQueries({ queryKey: trpc.league.pathKey() });
}

export function invalidateArticles(qc: QueryClient, trpc: Trpc) {
  return qc.invalidateQueries({ queryKey: trpc.articles.pathKey() });
}

/**
 * Signing in or out changes who everything is being asked as, so this one
 * genuinely does clear the lot.
 */
export function invalidateEverything(qc: QueryClient) {
  return qc.invalidateQueries();
}
